import { useEffect, useRef, useState, type TouchEvent } from "react";
import { Link } from "react-router-dom";
import { movies } from "@/mocks/movies";

const slides = movies.filter((movie) => movie.featured);

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [paused]);

  const goTo = (next: number) => {
    setIndex((next + slides.length) % slides.length);
  };

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 45) {
      goTo(index + (delta < 0 ? 1 : -1));
    }
    touchStartX.current = null;
  };

  const current = slides[index];

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-label="焦点内容轮播"
    >
      <div className="relative h-[560px] w-full md:h-[660px]">
        {slides.map((movie, slideIndex) => (
          <div
            key={movie.id}
            className={`absolute inset-0 transition-opacity duration-[900ms] ease-out ${
              slideIndex === index ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden={slideIndex !== index}
          >
            <img
              src={movie.backdrop}
              alt={`${movie.title}剧照`}
              title={`${movie.title} 影视详情`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        ))}

        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/70 to-primary-950/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/10 to-primary-950/40" />

        <div className="relative z-10 mx-auto flex h-full w-full max-w-7xl flex-col justify-end px-4 pb-16 md:px-6 md:pb-20">
          <div className="max-w-2xl">
            <h1 className="flex items-center gap-2 text-lg font-bold tracking-tight text-background-50 md:text-2xl">
              <span className="h-5 w-1 rounded-full bg-accent-500 md:h-6" />
              诱人的女员工hd影视专区
            </h1>
            <p className="mt-3 max-w-md text-xs leading-6 text-primary-200 md:text-sm">
              精选电影与电视剧推荐，剧情解析、演员资料与专题片单一站式浏览。
            </p>

            <div key={current.id} className="animate-fade-up mt-6 md:mt-8">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="rounded-sm bg-accent-500 px-2 py-1 text-[11px] font-medium text-primary-950">
                  {current.type}
                </span>
                {current.genres.map((genre) => (
                  <span
                    key={genre}
                    className="rounded-sm border border-background-50/25 px-2 py-1 text-[11px] text-background-50/90"
                  >
                    {genre}
                  </span>
                ))}
                <span className="flex items-center gap-1 rounded-sm bg-background-50/10 px-2 py-1 text-[11px] text-background-50 backdrop-blur-sm">
                  <i className="ri-star-fill text-accent-400 text-[10px] leading-none" />
                  <span className="font-label">{current.rating.toFixed(1)}</span>
                </span>
              </div>

              <h2 className="text-3xl font-bold tracking-tight text-background-50 md:text-5xl">
                {current.title}
              </h2>
              <p className="mt-3 text-sm leading-7 text-primary-100 md:text-base">「{current.tagline}」</p>
              <p className="mt-2 max-w-xl text-sm leading-7 text-primary-200/90 line-clamp-2 md:line-clamp-2">
                {current.summary}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${current.id}`}
                  className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400 cursor-pointer whitespace-nowrap"
                >
                  查看详情
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                </Link>
                <Link
                  to={`/category?genre=${encodeURIComponent(current.genres[0])}`}
                  className="inline-flex items-center gap-2 rounded-md border border-background-50/35 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer whitespace-nowrap"
                >
                  更多{current.genres[0]}作品
                </Link>
                <span className="hidden text-xs text-primary-300 sm:inline">
                  {current.year} · {current.region} · {current.duration} · 导演 {current.director}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 right-4 z-10 flex items-center gap-4 md:bottom-8 md:right-8">
          <div className="hidden items-center gap-3 md:flex">
            {slides.map((movie, slideIndex) => (
              <button
                key={movie.id}
                type="button"
                onClick={() => goTo(slideIndex)}
                aria-label={`切换到${movie.title}`}
                className="group flex items-center gap-2 cursor-pointer"
              >
                <span
                  className={`font-label text-[11px] transition-colors ${
                    slideIndex === index ? "text-accent-400" : "text-background-50/45 group-hover:text-background-50/80"
                  }`}
                >
                  {String(slideIndex + 1).padStart(2, "0")}
                </span>
                <span className="relative block h-0.5 w-8 overflow-hidden rounded-full bg-background-50/25">
                  <span
                    className={`absolute inset-y-0 left-0 bg-accent-500 transition-all duration-500 ${
                      slideIndex === index ? "w-full" : "w-0"
                    }`}
                  />
                </span>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => goTo(index - 1)}
              aria-label="上一张"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/30 text-background-50 transition-colors hover:bg-background-50 hover:text-primary-950 cursor-pointer"
            >
              <i className="ri-arrow-left-s-line w-5 h-5 flex items-center justify-center" />
            </button>
            <button
              type="button"
              onClick={() => goTo(index + 1)}
              aria-label="下一张"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/30 text-background-50 transition-colors hover:bg-background-50 hover:text-primary-950 cursor-pointer"
            >
              <i className="ri-arrow-right-s-line w-5 h-5 flex items-center justify-center" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}